import React from 'react';
import ReactDOM from 'react-dom/client';
import { SitesApp } from '@/SitesApp';
import '@/analytics/gtag-bootstrap';
import '@/index.css';
import { suppressConsoleNoise } from '@/utils/suppressConsoleNoise';
import { initAnalyticsLazy } from '@/analytics/firebaseAnalytics';
import { AnalyticsProvider } from '@/analytics';
import { isConsentGranted, subscribeToConsentChanges } from '@/analytics/consent';

// Hide noisy console chatter in production (e.g., iframe navigation, Chrome violations)
suppressConsoleNoise();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <AnalyticsProvider>
      <SitesApp />
    </AnalyticsProvider>
  </React.StrictMode>
);

// Defer analytics until idle or first interaction post-consent
try {
  let analyticsBootstrapped = false;

  const bootstrapAnalytics = () => {
    if (analyticsBootstrapped) return;
    analyticsBootstrapped = true;
    void initAnalyticsLazy();
  };

  const scheduleDeferredBootstrap = () => {
    if (analyticsBootstrapped) return;

    function cleanup() {
      window.removeEventListener('pointerdown', onFirstInteraction, true);
      window.removeEventListener('keydown', onFirstInteraction, true);
      window.removeEventListener('scroll', onFirstInteraction, true);
    }

    function onFirstInteraction() {
      cleanup();
      bootstrapAnalytics();
    }

    const idleTrigger = () => {
      cleanup();
      bootstrapAnalytics();
    };

    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(idleTrigger, { timeout: 4000 });
    } else {
      setTimeout(idleTrigger, 2000);
    }

    window.addEventListener('pointerdown', onFirstInteraction, { once: true, passive: true, capture: true } as any);
    window.addEventListener('keydown', onFirstInteraction, { once: true, capture: true } as any);
    window.addEventListener('scroll', onFirstInteraction, { once: true, passive: true, capture: true } as any);
  };

  if (isConsentGranted()) {
    scheduleDeferredBootstrap();
  } else {
    const unsubscribe = subscribeToConsentChanges((granted) => {
      if (!granted) return;
      unsubscribe();
      bootstrapAnalytics();
    });
  }
} catch {
  /* noop: analytics is optional */
}
